/**
 * Vault security health: password strength, reused, weak, and old passwords,
 * plus k-anonymous HIBP breach lookups.
 *
 * All analysis runs client-side over the already-decrypted vault. The HIBP
 * lookup sends only the first 5 hex digits of SHA-1 (k-anonymity): the password
 * never leaves the device. The network call is external and injectable so the
 * analysis can be tested without touching the network.
 */

import { sha256 } from "./primitives";

export interface StrengthResult {
  /** 0 (terrible) … 4 (excellent). */
  readonly score: 0 | 1 | 2 | 3 | 4;
  readonly entropyBits: number;
}

const SEQUENCES = ["0123456789", "abcdefghijklmnopqrstuvwxyz", "qwertyuiop"];

/** Pragmatic strength estimate (entropy per character set with penalties). */
export function estimateStrength(password: string): StrengthResult {
  if (password.length === 0) {
    return { score: 0, entropyBits: 0 };
  }
  let pool = 0;
  if (/[a-z]/.test(password)) {
    pool += 26;
  }
  if (/[A-Z]/.test(password)) {
    pool += 26;
  }
  if (/\d/.test(password)) {
    pool += 10;
  }
  if (/[^\dA-Za-z]/.test(password)) {
    pool += 32;
  }

  let entropy = password.length * Math.log2(Math.max(pool, 1));

  // Penalties: repetitions and sequences reduce effective entropy.
  const unique = new Set(password).size;
  if (unique <= 2) {
    entropy *= 0.4;
  } else if (unique / password.length < 0.5) {
    entropy *= 0.7;
  }
  const lower = password.toLowerCase();
  for (const seq of SEQUENCES) {
    for (let i = 0; i + 4 <= seq.length; i++) {
      if (lower.includes(seq.slice(i, i + 4))) {
        entropy *= 0.8;
        break;
      }
    }
  }

  let score: StrengthResult["score"];
  if (entropy < 28) {
    score = 0;
  } else if (entropy < 40) {
    score = 1;
  } else if (entropy < 60) {
    score = 2;
  } else if (entropy < 80) {
    score = 3;
  } else {
    score = 4;
  }
  return { score, entropyBits: Math.round(entropy) };
}

export interface PasswordEntry {
  readonly id: string;
  readonly password: string;
  readonly updatedAt?: string;
}

export interface HealthReport {
  readonly weak: string[];
  readonly reused: string[][];
  readonly old: string[];
}

export interface HealthOptions {
  readonly weakScoreThreshold?: number;
  readonly maxAgeDays?: number;
  readonly now?: number;
}

export function analyzeVaultHealth(
  entries: PasswordEntry[],
  options: HealthOptions = {},
): HealthReport {
  const { weakScoreThreshold = 2, maxAgeDays = 365, now = Date.now() } = options;

  const weak = entries
    .filter((e) => e.password && estimateStrength(e.password).score < weakScoreThreshold)
    .map((e) => e.id);

  const byPassword = new Map<string, string[]>();
  for (const e of entries) {
    if (!e.password) {
      continue;
    }
    const group = byPassword.get(e.password) ?? [];
    group.push(e.id);
    byPassword.set(e.password, group);
  }
  const reused = [...byPassword.values()].filter((g) => g.length > 1);

  const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
  const old = entries
    .filter((e) => e.updatedAt && now - Date.parse(e.updatedAt) > maxAgeMs)
    .map((e) => e.id);

  return { weak, reused, old };
}

/**
 * k-anonymous HIBP lookup. The password's `sha1Hex` is calculated separately
 * (HIBP's API requires SHA-1); here we expose the prefix to send and the check
 * against the range response, keeping the network outside the module.
 */
export function hibpSplit(sha1Hex: string): { prefix: string; suffix: string } {
  return { prefix: sha1Hex.slice(0, 5), suffix: sha1Hex.slice(5) };
}

/**
 * Given the HIBP range endpoint response (`SUFFIX:count` lines), reports how
 * many times the password appeared in breaches (0 = not found).
 */
export function hibpCountFromRange(rangeBody: string, suffix: string): number {
  for (const line of rangeBody.split(/\r?\n/)) {
    const [candidate, count] = line.split(":");
    if (candidate?.toUpperCase() === suffix.toUpperCase()) {
      return Number.parseInt(count ?? "0", 10);
    }
  }
  return 0;
}

/** Uppercase SHA-1 hex (HIBP format). Uses WebCrypto (SHA-1 available). */
export async function sha1HexUpper(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(text));
  let hex = "";
  for (const b of new Uint8Array(digest)) {
    hex += b.toString(16).padStart(2, "0");
  }
  return hex.toUpperCase();
}

// Re-export for the convenience of health consumers.
export { sha256 };
